import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Database } from "@/integrations/supabase/types";
import { Product } from "./useProducts";

export type WarehouseMovementType = Database["public"]["Enums"]["warehouse_movement_type"];

export interface WarehouseItem {
  id: string;
  product_id: string;
  organization_id: string;
  quantity: number;
  unit_price: number;
  min_stock_alert: number | null;
  created_at: string;
  updated_at: string;
  // Joined product data
  product?: Product;
  // Joined organization data
  organization?: {
    id: string;
    name: string;
  };
}

export interface WarehouseMovement {
  id: string;
  warehouse_item_id: string;
  movement_type: WarehouseMovementType;
  quantity: number;
  unit_price: number;
  property_id: string | null;
  notes: string | null;
  created_at: string;
  created_by: string;
}

export interface AddToWarehouseInput {
  product_id: string;
  organization_id: string;
  quantity: number;
  unit_price: number;
  min_stock_alert?: number | null;
  notes?: string;
}

export interface CreateMovementInput {
  warehouse_item_id: string;
  movement_type: WarehouseMovementType;
  quantity: number;
  unit_price: number;
  property_id?: string | null;
  notes?: string | null;
}

// Mapeo de tipos de movimiento a labels legibles
export const MOVEMENT_TYPE_LABELS: Record<WarehouseMovementType, string> = {
  entrada_compra: "Entrada por compra",
  salida_propiedad: "Salida a propiedad",
  ajuste_merma: "Ajuste / Merma",
  devolucion: "Devolución",
};

// Hook para obtener todos los items del almacén con sus productos y organizaciones
export const useWarehouseItems = () => {
  return useQuery({
    queryKey: ["warehouse-items"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("warehouse_items")
        .select(`
          *,
          product:products(*),
          organization:organizations(id, name)
        `)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as WarehouseItem[];
    },
  });
};

// Hook para agregar un producto al almacén (crea item + movimiento inicial)
export const useAddToWarehouse = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ product_id, organization_id, quantity, unit_price, min_stock_alert, notes }: AddToWarehouseInput) => {
      // 1. Crear el item del almacén
      const { data: warehouseItem, error: itemError } = await supabase
        .from("warehouse_items")
        .insert({
          product_id,
          organization_id,
          quantity,
          unit_price,
          min_stock_alert: min_stock_alert ?? null,
        })
        .select()
        .single();

      if (itemError) throw itemError;

      // 2. Obtener el usuario actual
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Usuario no autenticado");

      // 3. Crear el movimiento inicial de entrada
      const { error: movementError } = await supabase
        .from("warehouse_movements")
        .insert({
          warehouse_item_id: warehouseItem.id,
          movement_type: "entrada_compra",
          quantity,
          unit_price,
          notes: notes || "Entrada inicial al almacén",
          created_by: user.id,
        });

      if (movementError) throw movementError;

      return warehouseItem;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["warehouse-items"] });
      queryClient.invalidateQueries({ queryKey: ["warehouse-movements"] });
      queryClient.invalidateQueries({ queryKey: ["low-stock-alerts"] });
      toast({
        title: "Producto agregado al almacén",
        description: "El producto se ha agregado exitosamente",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error al agregar al almacén",
        description: error.message,
        variant: "destructive",
      });
    },
  });
};

// Hook para crear un movimiento de almacén
export const useCreateMovement = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (movement: CreateMovementInput) => {
      // 1. Obtener el usuario actual
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Usuario no autenticado");

      // 2. Crear el movimiento
      const { data: newMovement, error: movementError } = await supabase
        .from("warehouse_movements")
        .insert({
          ...movement,
          created_by: user.id,
        })
        .select()
        .single();

      if (movementError) throw movementError;

      // 3. Actualizar la cantidad en el item del almacén
      // Para entrada_compra y devolucion: sumar
      // Para salida_propiedad y ajuste_merma: restar
      const isAddition = movement.movement_type === "entrada_compra" || movement.movement_type === "devolucion";
      const quantityChange = isAddition ? movement.quantity : -movement.quantity;

      const { data: currentItem, error: fetchError } = await supabase
        .from("warehouse_items")
        .select("quantity")
        .eq("id", movement.warehouse_item_id)
        .single();

      if (fetchError) throw fetchError;

      const newQuantity = Math.max(0, currentItem.quantity + quantityChange);

      const { error: updateError } = await supabase
        .from("warehouse_items")
        .update({ quantity: newQuantity })
        .eq("id", movement.warehouse_item_id);

      if (updateError) throw updateError;

      return newMovement;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["warehouse-items"] });
      queryClient.invalidateQueries({ queryKey: ["warehouse-movements", variables.warehouse_item_id] });
      queryClient.invalidateQueries({ queryKey: ["low-stock-alerts"] });
      
      const label = MOVEMENT_TYPE_LABELS[variables.movement_type];
      toast({
        title: "Movimiento registrado",
        description: `${label} registrada exitosamente`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error al registrar movimiento",
        description: error.message,
        variant: "destructive",
      });
    },
  });
};

// Hook para obtener movimientos de un item específico
export const useWarehouseMovements = (warehouseItemId: string | null) => {
  return useQuery({
    queryKey: ["warehouse-movements", warehouseItemId],
    queryFn: async () => {
      if (!warehouseItemId) return [];
      
      const { data, error } = await supabase
        .from("warehouse_movements")
        .select("*")
        .eq("warehouse_item_id", warehouseItemId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as WarehouseMovement[];
    },
    enabled: !!warehouseItemId,
  });
};

// Hook para actualizar un item del almacén
export const useUpdateWarehouseItem = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<WarehouseItem> & { id: string }) => {
      const { data, error } = await supabase
        .from("warehouse_items")
        .update(updates)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["warehouse-items"] });
      queryClient.invalidateQueries({ queryKey: ["low-stock-alerts"] });
      toast({
        title: "Item actualizado",
        description: "El item del almacén se ha actualizado exitosamente",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error al actualizar item",
        description: error.message,
        variant: "destructive",
      });
    },
  });
};

// Hook para eliminar un item del almacén
export const useDeleteWarehouseItem = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("warehouse_items")
        .delete()
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["warehouse-items"] });
      queryClient.invalidateQueries({ queryKey: ["low-stock-alerts"] });
      toast({
        title: "Item eliminado",
        description: "El item del almacén se ha eliminado exitosamente",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error al eliminar item",
        description: error.message,
        variant: "destructive",
      });
    },
  });
};
